import * as React from 'react';

import styles from './SignatoryList.module.css';
import { SignerFormApi, isActiveSigner } from './useSignerForm';
import { DeadlineCard } from '../../../../components/synced/ui/DeadlineCard/DeadlineCard';
import { SegmentedControl } from '../../../../components/synced/ui/SegmentedControl/SegmentedControl';
import { SignatoryCard } from '../../../../components/synced/ui/SignatoryCard/SignatoryCard';
import { NewButton } from '../../../../components/synced/ui/NewButton/NewButton';

interface SignatoryListProps {
  api: SignerFormApi;
  selectedIndex: number;
  onSelect: (index: number) => void;
  contractorTasksDueDate?: Date;
  contractorSignDueDate?: Date;
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const deadlineNote = (date?: Date): { note: string; bg: string; color: string } | undefined => {
  if (!date) return undefined;
  const due = new Date(date);
  const today = new Date();
  due.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);
  const days = Math.round((due.getTime() - today.getTime()) / MS_PER_DAY);
  if (days < 0) {
    return { note: `Просрочено на ${Math.abs(days)} дн.`, bg: 'var(--deadline-overdue-bg)', color: 'var(--deadline-overdue-color)' };
  }
  if (days === 0) {
    return { note: 'Сегодня', bg: 'var(--deadline-pending-bg)', color: 'var(--deadline-pending-color)' };
  }
  return { note: `Осталось ${days} дн.`, bg: 'var(--deadline-pending-bg)', color: 'var(--deadline-pending-color)' };
};

const formatDate = (date?: Date): string => date ? new Date(date).toLocaleDateString('ru-RU') : '—';

export const SignatoryList: React.FC<SignatoryListProps> = ({ api, selectedIndex, onSelect, contractorTasksDueDate, contractorSignDueDate }) => {
  const [addOpen, setAddOpen] = React.useState(false);
  const tasksNote = deadlineNote(contractorTasksDueDate);
  const signNote = deadlineNote(contractorSignDueDate);

  const activeSigners = api.signers.filter(isActiveSigner);

  const readyCount = activeSigners.filter(s => {
    const c = api.completionOf(s);
    return c.total > 0 && c.filled >= c.total;
  }).length;

  return (
    <div className={styles.root}>
      <div className={styles.deadlines}>
        <DeadlineCard
          label="Предоставление ИД КА"
          date={formatDate(contractorTasksDueDate)}
          note={tasksNote?.note}
          noteBg={tasksNote?.bg}
          noteColor={tasksNote?.color} />
        <DeadlineCard
          label="Подписание ИД КА"
          date={formatDate(contractorSignDueDate)}
          note={signNote?.note}
          noteBg={signNote?.bg}
          noteColor={signNote?.color} />
      </div>

      <div>
        <div className={styles.sectionLabel}>Тип документооборота</div>
        <SegmentedControl
          options={[
            { key: 'electronic', label: 'Электронный', icon: 'fa-solid fa-bolt' },
            { key: 'paper', label: 'Бумажный', icon: 'fa-regular fa-file-lines' },
          ]}
          value={api.isElectronic ? 'electronic' : 'paper'}
          onChange={(k) => !api.readonly && api.setElectronic(k === 'electronic')} />
      </div>

      <div>
        <div className={styles.partHeader}>
          <span className={styles.sectionLabel}>Участники подписания</span>
          <span className={styles.readyLabel}>Готово {readyCount} из {activeSigners.length}</span>
        </div>
        <div className={styles.cards}>
          {api.signers.map((s, index) => {
            const c = api.completionOf(s);
            const rep = [s.RepLastName, s.RepFirstName, s.RepMiddleName].filter(Boolean).join(' ');
            return (
              <SignatoryCard
                key={index + '-' + s.RoleId}
                sub={api.getSubLabel(s)}
                orgName={s.OrgName || undefined}
                repLine={rep || s.RepJobTitle || undefined}
                selected={index === selectedIndex}
                filled={c.filled}
                saved={!api.isSignerDirty(index)}
                total={c.total}
                onSelect={() => onSelect(index)} />
            );
          })}
        </div>

        {!api.readonly && (
          <div className={styles.addWrap}>
            <NewButton
              variant="dashed"
              block
              icon="fa-solid fa-plus"
              disabled={api.isLoading}
              onClick={() => setAddOpen(o => !o)}>
              Добавить участника
            </NewButton>
            {addOpen && (
              <div className={styles.addMenu}>
                <div
                  className={`${styles.addItem}${api.actTypes.length === 0 ? ` ${styles.addItemDisabled}` : ''}`}
                  onClick={() => { if (api.actTypes.length > 0) { api.addActParty(); setAddOpen(false); onSelect(api.signers.length); } }}>
                  <i className="fa-solid fa-helmet-safety" />Лицо, выполнившее работы
                </div>
                <div className={styles.addItem} onClick={() => { api.addOther(); setAddOpen(false); onSelect(api.signers.length); }}>
                  <i className="fa-regular fa-building" />Иное участвующее лицо
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
